import { Injectable } from "@angular/core";
import * as FileSaver from "file-saver";
import { BehaviorSubject, firstValueFrom } from "rxjs";
import { HttpService } from "src/app/session/services/http.service";
import { environment } from "src/environments/environment";

import { MultiSendByEmail } from "../../model/document-to-Share";
import { InspectionDocument } from "../../model/inspection-document";

@Injectable({
  providedIn: "root",
})
export class InspectionDocumentService {
  public documentsToShareByEmail = new BehaviorSubject<InspectionDocument[]>(
    null,
  );
  public restartCheckedDocumentsObservable = new BehaviorSubject<any>(false);

  public constructor(private readonly httpService: HttpService) {}

  public updateDocumentsToShareByEmail(documents: any[]) {
    this.documentsToShareByEmail.next(documents);
  }

  public restartCheckedDocuments() {
    this.restartCheckedDocumentsObservable.next(true);
  }

  public async uploadDocuments(inspectionId: string, files: File[]) {
    for (const file of files) {
      await this.uploadDocument(inspectionId, file);
    }
  }

  public async uploadDocument(inspectionId: string, file: File) {
    const formData = this.createFormData(inspectionId, file);
    await this.httpService
      .post(
        `${environment.webApiUrl}/api/InspectionDocuments/Document`,
        formData,
        { withCredentials: true },
      )
      .toPromise();
  }

  public async getDocumentList(inspectionId: string): Promise<InspectionDocument[]> {
    return this.httpService
      .get(
        `${environment.webApiUrl}/api/InspectionDocuments/DocumentList?inspectionId=${inspectionId}`,
        { withCredentials: true },
      )
      .toPromise();
  }

  public async sendByEmail(multiSendByEmail: MultiSendByEmail) {
    return this.httpService
      .post(
        `${environment.webApiUrl}/api/inspectionDocuments/sendDocument`,
        multiSendByEmail,
        { withCredentials: true },
      )
      .toPromise();
  }

  public async downloadDocument(
    inspectionId: string,
    inspectionDocument: InspectionDocument,
  ) {
    const blob = await this.httpService
      .get(
        `${environment.webApiUrl}/api/InspectionDocuments/Download?inspectionId=${inspectionId}&documentId=${inspectionDocument.documentId}`,
        { responseType: "blob", withCredentials: true },
      )
      .toPromise();

    FileSaver.saveAs(blob, inspectionDocument.name);
  }

    public async getDocument(inspectionId: string, documentId : string): Promise<Blob> {
    const blobDocument = await firstValueFrom(this.httpService.get(
      `${environment.webApiUrl}/api/InspectionDocuments/Download?inspectionId=${inspectionId}&documentId=${documentId}`,
      { responseType: "blob", withCredentials: true },
    ));
    
    return blobDocument;
  }
  public async updateSharedDocument(
    inspectionId: string,
    documentId: string,
  ): Promise<void> {
    await this.httpService
      .put(
        `${environment.webApiUrl}/api/InspectionDocuments/Document?inspectionId=${inspectionId}&documentId=${documentId}`,
        { withCredentials: true },
      )
      .toPromise();
  }

  public async deleteDocument(
    inspectionId: string,
    inspectionDocument: InspectionDocument,
  ) {
    await this.httpService
      .delete(
        `${environment.webApiUrl}/api/InspectionDocuments/Document?documentId=${inspectionDocument.documentId}&inspectionId=${inspectionId}`,
        { withCredentials: true },
      )
      .toPromise();
  }

  public async previewDocument(inspectionId: string, documentId : string): Promise<Blob> {
    const blobDocument = await firstValueFrom(this.httpService.get(
      `${environment.webApiUrl}/api/InspectionDocuments/Preview?inspectionId=${inspectionId}&documentId=${documentId}`,
      { responseType: "blob", observe: 'response', withCredentials: true },
    ));
    
    return blobDocument;
  }

  private createFormData(inspectionId: string, file: File) {
    const formData = new FormData();
    formData.set("inspectionId", inspectionId);
    formData.set("file", file);
    return formData;
  }
}
